import React, { useEffect, useMemo, useState, useRef } from 'react';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import S from './Widget.module.scss';
import WidgetHeaderControls from './WidgetHeaderControls';
import MapPreview from './MapPreview';
import AffichageVue from '../../Vue/AffichageVue/AffichageVue';

const ORIGINAL_WIDTH = 1400;
const ORIGINAL_HEIGHT = 900;
const DEFAULT_CONTAINER_WIDTH = 480;

const WidgetPlan = ({
  vuesFavorites = [],
  onVueClick,
  headerContent = null,
  showHeader = true,
  viewId = null, // ID da vista para renderizar AffichageVue diretamente
}) => {
  const hasVue = vuesFavorites && vuesFavorites.length > 0;
  const [selectedVueIndex, setSelectedVueIndex] = useState('0');
  const vuesLength = vuesFavorites?.length || 0;

  const currentVue = hasVue
    ? vuesFavorites[Number(selectedVueIndex)] || vuesFavorites[0]
    : null;

  const resolvedViewId = viewId || currentVue?.view_id;

  // Refs e estados
  const widgetRef = useRef(null);
  const miniatureContainerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(DEFAULT_CONTAINER_WIDTH);
  const [isVisible, setIsVisible] = useState(false);

  // Só montar AffichageVue quando o widget entrar no viewport
  useEffect(() => {
    const el = widgetRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Observar mudanças no tamanho do container — só após isVisible
  useEffect(() => {
    if (!isVisible) return;
    const container = miniatureContainerRef.current;
    if (!container) return;

    const updateWidth = () => {
      const width = container.offsetWidth;
      if (width > 0) setContainerWidth(width);
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!hasVue) {
      setSelectedVueIndex('0');
      return;
    }

    const currentIndex = Number(selectedVueIndex);
    if (currentIndex >= vuesLength) {
      setSelectedVueIndex('0');
    }
  }, [hasVue, vuesLength, selectedVueIndex]);

  const vueOptions = useMemo(() => {
    if (!hasVue || vuesLength < 2) {
      return [];
    }

    return vuesFavorites.map((vue, index) => ({
      value: String(index),
      label: vue.view_name || `${checkMot('plan')} ${index + 1}`,
    }));
  }, [hasVue, vuesFavorites, vuesLength]);

  const resolveCenter = (vue) => {
    if (!vue) return null;
    if (Array.isArray(vue.center) && vue.center.length === 2) return vue.center;
    if (vue.lat && vue.lng) return [vue.lat, vue.lng];
    if (vue.latitude && vue.longitude) return [vue.latitude, vue.longitude];
    if (vue.center_lat && vue.center_lng)
      return [vue.center_lat, vue.center_lng];
    return null;
  };

  const mapCenter = resolveCenter(currentVue) || [48.8566, 2.3522];
  const mapZoom = currentVue?.zoom || 12;

  const resolvedHeader =
    headerContent ||
    (showHeader ? (
      <WidgetHeaderControls
        titleKey="plan"
        selectOptions={vueOptions}
        selectedValue={selectedVueIndex}
        onSelectChange={setSelectedVueIndex}
      />
    ) : null);

  // Renderizar miniatura do AffichageVue
  const renderPlanContent = () => {
    if (resolvedViewId) {
      const scale = containerWidth / ORIGINAL_WIDTH;
      const scaledHeight = ORIGINAL_HEIGHT * scale;

      // Skeleton com tamanho fixo enquanto o widget ainda não entrou no viewport
      if (!isVisible) {
        const defaultHeight = (DEFAULT_CONTAINER_WIDTH / ORIGINAL_WIDTH) * ORIGINAL_HEIGHT;
        return (
          <div
            ref={miniatureContainerRef}
            className={S.dashboardMiniatureContainer}
            style={{ height: `${defaultHeight}px` }}
          >
            <div className={S.miniatureSkeleton} />
          </div>
        );
      }

      return (
        <div
          ref={miniatureContainerRef}
          className={S.dashboardMiniatureContainer}
          style={{ height: `${scaledHeight}px` }}
        >
          <div
            className={S.dashboardMiniatureInner}
            style={{
              width: `${ORIGINAL_WIDTH}px`,
              height: `${ORIGINAL_HEIGHT}px`,
              transform: `scale(${scale})`,
            }}
          >
            <AffichageVue
              key={`plan-${resolvedViewId}`}
              view_id={resolvedViewId}
              mapContainerId={`mapPlan-${resolvedViewId}`}
            />
          </div>
        </div>
      );
    }

    // Fallback para MapPreview se não tiver view_id
    if (hasVue) {
      return (
        <div className={S.planContainer}>
          <div className={S.planPreview}>
            <MapPreview
              center={mapCenter}
              zoom={mapZoom}
              className={S.leafletContainer}
              interactive={true}
            />
          </div>
        </div>
      );
    }

    // Empty state
    return (
      <div className={S.emptyState}>
        <div className={S.mapPlaceholder}>
          <MapPreview
            center={[48.8566, 2.3522]}
            zoom={3}
            className={S.leafletContainer}
            interactive={true}
          />
        </div>
      </div>
    );
  };

  return (
    <div ref={widgetRef} className={[S.widget, S.widgetLarge].join(' ')}>
      {resolvedHeader}
      <div className={S.widgetContent}>
        {renderPlanContent()}
      </div>
    </div>
  );
};

export default WidgetPlan;
